/**
 * ChatMessage.tsx — routing surface (Phase 1b rebuild)
 *
 * This file was previously a 825-line monolith. It is now a thin routing
 * component that maps the message role/error flag to the appropriate
 * subcomponent in components/chat/.
 *
 * Existing callsites (App.tsx + 5 test files) continue to import from this
 * path unchanged — the prop shape is backwards-compatible.
 *
 * @deprecated Prefer importing UserMessage or AssistantMessage directly for
 * new code. This shim is kept for test-file and App.tsx compatibility.
 */

import type { ChatMessage as ChatMessageType, AuthUser } from "../types/index.js";
import UserMessage from "./chat/UserMessage.js";
import AssistantMessage from "./chat/AssistantMessage.js";

export interface ChatMessageProps {
	message: ChatMessageType;
	/** True only for the last assistant message while the stream is active. */
	isStreaming?: boolean;
	/** The authenticated user — used to render a profile picture on user messages. */
	user?: AuthUser;
	/** Called when the user approves an action proposal. */
	onApproveAction?: (actionId: string) => void;
	/** Called when the user rejects an action proposal. */
	onRejectAction?: (actionId: string) => void;
}

/**
 * Routes a chat message to the appropriate renderer.
 *
 * - role === "user"  → UserMessage (current-user or other-user branch)
 * - isError          → AssistantMessage variant="error"
 * - all others       → AssistantMessage (States A–D)
 */
export default function ChatMessage({
	message,
	isStreaming = false,
	user,
	onApproveAction,
	onRejectAction,
}: ChatMessageProps) {
	if (message.role === "user") {
		return <UserMessage message={message} user={user} />;
	}
	if (message.isError) {
		return (
			<AssistantMessage
				message={message}
				isStreaming={isStreaming}
				variant="error"
				onApproveAction={onApproveAction}
				onRejectAction={onRejectAction}
			/>
		);
	}
	return (
		<AssistantMessage
			message={message}
			isStreaming={isStreaming}
			onApproveAction={onApproveAction}
			onRejectAction={onRejectAction}
		/>
	);
}
